import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useModule } from '../../context/ModuleContext';
import { FaHome, FaChartBar, FaBoxOpen, FaUser, FaHandshake, FaStore, FaMapMarkerAlt } from 'react-icons/fa';

const G = '#1A6B3C';

// Nav items per module per role
const MODULE_NAV = {
  agri: {
    seller: [
      { path:'/', icon:<FaHome />, label:'Home' },
      {
        path: "/my-crops",
        icon: (
          <img
            src="/my-crops.jpg"
            alt="My Crops"
            style={{ width: 20, height: 20, objectFit: "contain" }}
          />
        ),
        label: "My Crops",
      },
      { path: "/add-crop", isAdd: true, label: "Add" },
      { path:'/requests/incoming', icon:<FaBoxOpen />, label:'Requests' },
      { path:'/profile',           icon:<FaUser />,    label:'Profile', badge:true },
    ],
    buyer: [
      { path:'/',              icon:<FaHome />,     label:'Home' },
      { path:'/buyer',         icon:<FaChartBar />, label:'Dashboard' },
      { path:'/requests/new',  isAdd:true,          label:'Request' },
      { path:'/requests/my',   icon:<FaBoxOpen />,  label:'My Req.', badge:true },
      { path:'/profile',       icon:<FaUser />,     label:'Profile' },
    ],
    agent: [
      { path:'/',              icon:<FaHome />,      label:'Home' },
      { path:'/agent',         icon:<FaHandshake />, label:'Dashboard' },
      { path:'/requests/new',  isAdd:true,           label:'Request' },
      { path:'/requests/my',   icon:<FaBoxOpen />,   label:'My Req.' },
      { path:'/profile',       icon:<FaUser />,      label:'Profile', badge:true },
    ],
  },
  nursery: {
    default: [
      { path:'/nursery',          icon:<FaHome />,  label:'Home' },
      { path:'',                  isNearMe:true,    label:'Nearby' },
      { path:'/nursery/list',     icon:<FaStore />, label:'Nurseries', badge:true },
      { path:'/profile',          icon:<FaUser />,  label:'Profile' },
    ],
  },
  land: {
    default: [
      { path:'/land',              icon:<FaHome />,        label:'Home' },
      { path:'/land/my-listings',  icon:<FaBoxOpen />,     label:'My Listings' },
      { path:'/land/add',          isAdd:true,             label:'Add Property' },
      { path:'/land/messages',     icon:<FaHandshake />,   label:'Messages', badge:true },
      { path:'/profile',           icon:<FaUser />,        label:'Profile' },
    ],
  },
};

export default function BottomNav() {
  const navigate         = useNavigate();
  const { pathname }     = useLocation();
  const { user }         = useAuth();
  const { unreadCount }  = useNotifications();
  const { activeModule } = useModule();

  const moduleId = activeModule?.id || 'agri';
  const color    = activeModule?.color || G;

  const getItems = () => {
    const moduleNavs = MODULE_NAV[moduleId] || MODULE_NAV.agri;
    const role = user?.role || 'default';
    return moduleNavs[role] || moduleNavs.default || MODULE_NAV.agri.default;
  };

  const items = getItems() || [];

  const isActive = (item) => {
    if (!item.path) return false;
    if (item.path === '/') return pathname === '/';
    return pathname === item.path || pathname.startsWith(item.path);
  };

  // Shared sizing constants so every tab (icon, label, hit area, active
  // highlight) lines up on the same grid regardless of label length or icon type.
  const NAV_HEIGHT   = 64;   // fixed bar height (excludes safe-area inset)
  const ICON_SIZE    = 20;
  const LABEL_SIZE   = 11;
  const HILITE_W     = 60;
  const HILITE_H     = 46;

  return (
    <nav style={{
      position:'fixed', bottom:0, left:0, right:0, zIndex:200,
      height:`calc(${NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
      boxSizing:'border-box',
      background:color, borderTopLeftRadius:22, borderTopRightRadius:22,
      boxShadow:'0 -4px 16px rgba(0,0,0,.18)',
      display:'flex', alignItems:'center', justifyContent:'space-around',
      padding:'0 6px', paddingBottom:'env(safe-area-inset-bottom, 0px)',
      fontFamily:'Inter,system-ui,sans-serif',
    }}>
      {items.map((item, idx) => {

        if (item.isNearMe) return (
          <button key={idx} onClick={() => {
            const city = user?.locationAddress?.split(',')[0]?.trim();
            navigate('/nursery/list' + (city ? '?city=' + encodeURIComponent(city) : ''));
          }}
            style={{
              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4,
              background:'none', border:'none', cursor:'pointer', flex:1,
              height:NAV_HEIGHT, minWidth:0, padding:0,
            }}>
            <FaMapMarkerAlt style={{ fontSize:ICON_SIZE, color:'rgba(255,255,255,.75)' }} />
            <span style={{ fontSize:LABEL_SIZE, color:'rgba(255,255,255,.75)', fontWeight:500 }}>Nearby</span>
          </button>
        );

        if (item.isAdd) return (
          <button key={idx}
            onClick={() => navigate(item.path)}
            style={{
              width:52, height:52, borderRadius:'50%', background:color, color:'white',
              border:'4px solid white', boxShadow:'0 4px 14px rgba(0,0,0,.28)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:22, fontWeight:400, cursor:'pointer', marginTop:-(52/2 + 8), flexShrink:0,
            }}>
            ＋
          </button>
        );

        const active = isActive(item);
        const badge  = item.badge && unreadCount > 0;

        return (
          <button key={idx}
            onClick={() => navigate(item.path)}
            style={{
              display:'flex', alignItems:'center', justifyContent:'center',
              background:'none', border:'none', cursor:'pointer', flex:1,
              height:NAV_HEIGHT, minWidth:0, padding:0,
            }}>
            <span style={{
              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:3,
              width:HILITE_W, height:HILITE_H, boxSizing:'border-box',
              background: active ? 'rgba(255,255,255,.16)' : 'none',
              borderRadius:14,
            }}>
              <span style={{ position:'relative', width:ICON_SIZE, height:ICON_SIZE, fontSize:ICON_SIZE, lineHeight:1, display:'flex', alignItems:'center', justifyContent:'center', color:'white' }}>
                {item.icon}
                {badge && (
                  <span style={{
                    position:'absolute', top:-6, right:-8,
                    background:'#DC2626', color:'white', borderRadius:'50%',
                    width:14, height:14, fontSize:8, fontWeight:700,
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </span>
              <span style={{ fontSize:LABEL_SIZE, fontWeight: active ? 700 : 500, color:'white', whiteSpace:'nowrap' }}>
                {item.label}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}